import { tactiques } from './cadreReferentielBZ';
import { livrablesTangiblesParTactique } from './livrablesTangiblesParTactique';

export type SimpleCadreBZPilierId =
  | 'gouvernance-strategie-organisation'
  | 'architecture-infrastructure-cloud'
  | 'applications-donnees-integrations'
  | 'services-operations-experience'
  | 'cybersecurite-risque-conformite'
  | 'transformation-innovation-ia';

export type SimpleCadreBZTransversalId =
  | 'actifs-parties-prenantes'
  | 'projets-amelioration-continue';

export type SimpleCadreBZAssociation = {
  tactiqueId: string;
  simplePilierId: SimpleCadreBZPilierId;
  transversaleIds: SimpleCadreBZTransversalId[];
  rationale: string;
};

export const cadreBZSimplePiliers: {
  id: SimpleCadreBZPilierId;
  nom: string;
  description: string;
}[] = [
  {
    id: 'gouvernance-strategie-organisation',
    nom: 'Gouvernance, stratégie & organisation',
    description: 'Décisions, vision, rôles, priorités, budget, politiques et alignement direction.',
  },
  {
    id: 'architecture-infrastructure-cloud',
    nom: 'Architecture, infrastructure & cloud',
    description: 'Fondations technologiques, réseaux, postes, plateformes, capacité, cloud et résilience.',
  },
  {
    id: 'applications-donnees-integrations',
    nom: 'Applications, données & intégrations',
    description: 'Patrimoine applicatif, données, licences, flux, ownership applicatif et qualité informationnelle.',
  },
  {
    id: 'services-operations-experience',
    nom: 'Services, opérations & expérience utilisateur',
    description: 'Support, incidents, demandes, changements, disponibilité, SLA et qualité perçue.',
  },
  {
    id: 'cybersecurite-risque-conformite',
    nom: 'Cybersécurité, risque & conformité',
    description: 'Posture cyber, contrôles, identités, risques, conformité, détection, réponse et reprise.',
  },
  {
    id: 'transformation-innovation-ia',
    nom: 'Transformation, innovation & IA',
    description: 'Modernisation, automatisation, veille, expérimentation, IA, DevSecOps et adoption.',
  },
];

export const cadreBZTransversales: {
  id: SimpleCadreBZTransversalId;
  nom: string;
  description: string;
}[] = [
  {
    id: 'actifs-parties-prenantes',
    nom: 'Gestion des actifs & parties prenantes',
    description: 'Inventaires, owners, utilisateurs, accès, dépendances, fournisseurs et visibilité 360 degrés.',
  },
  {
    id: 'projets-amelioration-continue',
    nom: 'Gestion de projet & amélioration continue',
    description: 'Roadmaps, portefeuille, priorisation, exécution, post-mortem, KPI et progression continue.',
  },
];

const byPilier: Record<number, SimpleCadreBZPilierId> = {
  1: 'gouvernance-strategie-organisation',
  2: 'architecture-infrastructure-cloud',
  3: 'gouvernance-strategie-organisation',
  4: 'services-operations-experience',
  5: 'cybersecurite-risque-conformite',
  6: 'transformation-innovation-ia',
  7: 'transformation-innovation-ia',
};

const specificPilierOverrides: Partial<Record<string, SimpleCadreBZPilierId>> = {
  'P2-T02': 'gouvernance-strategie-organisation',
  'P2-T03': 'cybersecurite-risque-conformite',
  'P2-T04': 'applications-donnees-integrations',
  'P2-T06': 'cybersecurite-risque-conformite',
  'P2-T07': 'cybersecurite-risque-conformite',
  'P2-T09': 'applications-donnees-integrations',
  'P3-T09': 'transformation-innovation-ia',
  'P4-T05': 'services-operations-experience',
  'P4-T06': 'architecture-infrastructure-cloud',
  'P4-T10': 'services-operations-experience',
  'P7-T06': 'applications-donnees-integrations',
  'P7-T07': 'transformation-innovation-ia',
};

const projectTactiques = new Set([
  'P1-T02',
  'P1-T06',
  'P1-T10',
  'P3-T01',
  'P3-T02',
  'P3-T03',
  'P3-T04',
  'P3-T05',
  'P3-T06',
  'P3-T07',
  'P3-T08',
  'P3-T09',
  'P3-T10',
  'P4-T05',
  'P6-T02',
  'P6-T04',
  'P6-T05',
  'P6-T06',
  'P6-T08',
  'P7-T02',
  'P7-T03',
  'P7-T06',
  'P7-T07',
  'P7-T09',
]);

const assetTactiques = new Set([
  'P1-T09',
  'P2-T01',
  'P2-T02',
  'P2-T03',
  'P2-T04',
  'P2-T05',
  'P2-T06',
  'P2-T07',
  'P2-T08',
  'P2-T09',
  'P2-T10',
  'P4-T06',
  'P5-T02',
  'P5-T03',
  'P5-T04',
  'P5-T06',
]);

const rationaleByLegacyPilier: Record<number, string> = {
  1: 'Ancienne tactique de stratégie/gouvernance: elle nourrit le pilotage exécutif du nouveau cadre.',
  2: 'Ancienne tactique d’actifs/utilisateurs: elle devient une capacité transverse, avec rattachement au pilier technique ou cyber le plus naturel.',
  3: 'Ancienne tactique de portefeuille/projets: elle devient principalement une mécanique transverse de gestion de projet.',
  4: 'Ancienne tactique de services TI: elle alimente le pilier services, opérations et expérience utilisateur.',
  5: 'Ancienne tactique cybersécurité/NIST: elle reste dans le pilier cyber, risque et conformité.',
  6: 'Ancienne tactique d’amélioration continue: elle alimente la transformation et la dimension transverse d’amélioration continue.',
  7: 'Ancienne tactique innovation/IA: elle alimente le pilier transformation, innovation et IA.',
};

function getTransversalesForTactique(tactiqueId: string): SimpleCadreBZTransversalId[] {
  const transversales: SimpleCadreBZTransversalId[] = [];
  if (assetTactiques.has(tactiqueId)) transversales.push('actifs-parties-prenantes');
  if (projectTactiques.has(tactiqueId)) transversales.push('projets-amelioration-continue');
  return transversales;
}

export const associationsTactiquesCadreBZ: SimpleCadreBZAssociation[] = tactiques.map((tactique) => ({
  tactiqueId: tactique.id,
  simplePilierId: specificPilierOverrides[tactique.id] ?? byPilier[tactique.pilier],
  transversaleIds: getTransversalesForTactique(tactique.id),
  rationale: rationaleByLegacyPilier[tactique.pilier],
}));

export const associationsByTactiqueId = new Map(
  associationsTactiquesCadreBZ.map((association) => [association.tactiqueId, association]),
);

export const tactiquesAvecCadreSimple = tactiques.map((tactique) => {
  const association = associationsByTactiqueId.get(tactique.id);
  return {
    ...tactique,
    simplePilierId: association?.simplePilierId ?? byPilier[tactique.pilier],
    transversaleIds: association?.transversaleIds ?? [],
    mappingRationale: association?.rationale ?? '',
  };
});

export const livrablesAvecCadreSimple = livrablesTangiblesParTactique.map((livrable) => {
  const association = associationsByTactiqueId.get(livrable.tactiqueId);
  return {
    ...livrable,
    simplePilierId: association?.simplePilierId ?? 'gouvernance-strategie-organisation',
    transversaleIds: association?.transversaleIds ?? [],
  };
});

export const cadreBZMappingStats = {
  tactiques: tactiquesAvecCadreSimple.length,
  livrables: livrablesAvecCadreSimple.length,
  piliers: cadreBZSimplePiliers.length,
  transversales: cadreBZTransversales.length,
};

export function getSimplePilierName(id: SimpleCadreBZPilierId): string {
  return cadreBZSimplePiliers.find((pilier) => pilier.id === id)?.nom ?? id;
}

export function getTransversaleName(id: SimpleCadreBZTransversalId): string {
  return cadreBZTransversales.find((transversale) => transversale.id === id)?.nom ?? id;
}
